import '../../styles/_time-card.scss';
import '../../styles/_typography.scss';

import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { CleTimeRecord } from '../../datasource/generated';
import { useAppSelector } from '../../hooks';
import useChangeSegment from '../../hooks/useChangeSegment';
import { useProgramDetails } from '../../hooks/useProgramDetails';
import { useTimeInterval } from '../../hooks/useTimeInterval';
import { getOnDemandCreditPercentComplete } from '../../utility/creditProgress';
import { PercentCircle } from '../PercentCircle/PercentCircle';

export type ITimeCardProps = {
    timeRecord: CleTimeRecord;
    onClose: (() => void) | null;
    isSingleSegment: boolean;
};

const ONE_MINUTE_MS = 60 * 1000;

const PLAYBACK_THRESHOLD_PERCENT = 99;
const IN_PROGRESS_CLE_CLAMP_PERCENTAGE = 95;

export const TimeCard = ({ timeRecord, onClose, isSingleSegment } : ITimeCardProps) => {
    const { changeSegment } = useChangeSegment();
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const recordProgress = useAppSelector((state) => state.player.playbackProgress.find(it => it.itemSk === timeRecord.item_SK), shallowEqual);
    const { data: programDetails } = useProgramDetails(launchDetails?.itemPk ?? '0');
    const currentTime = useTimeInterval(ONE_MINUTE_MS, !launchDetails?.isWebcast);
    const recordProgressPosition = recordProgress?.position;
    const recordProgressTotalTime = recordProgress?.totalTime;
    const onDemandProgress = useMemo(() => {
        return recordProgressPosition == null
            || recordProgressTotalTime == null
            || !Number.isFinite(recordProgressPosition)
            || !Number.isFinite(recordProgressTotalTime)
            || recordProgressPosition < 0
            || recordProgressTotalTime <= 0
            ? undefined
            : { position: recordProgressPosition, totalTime: recordProgressTotalTime };
    }, [recordProgressPosition, recordProgressTotalTime]);

    const liveSegments = useMemo(() => {
        return (programDetails?.dailySegments ?? []).flatMap(it => it.segments ?? []);
    }, [programDetails?.dailySegments]);

    const liveSegment = useMemo(() => {
        return liveSegments.find(it => it.programSegmentSk === timeRecord.prgSegment_SK);
    }, [liveSegments, timeRecord.prgSegment_SK]);

    const livePercentComplete = useMemo(() => {
        const startMs = new Date(liveSegment?.startTimeUtc ?? '').getTime();
        const endMs = new Date(liveSegment?.endTimeUtc ?? '').getTime();

        if (!liveSegment || !Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs)
            return timeRecord.percentComplete ?? 0;

        const totalDuration = endMs - startMs;
        const remainingTime = endMs - currentTime;

        if (currentTime < startMs)
            return 0;

        return totalDuration * (1 - PLAYBACK_THRESHOLD_PERCENT/100) < remainingTime
            ? Math.min(timeRecord.percentComplete ?? 0, IN_PROGRESS_CLE_CLAMP_PERCENTAGE)
            : timeRecord.percentComplete ?? 0;
    }, [
        currentTime,
        liveSegment,
        timeRecord.percentComplete,
    ]);

    const onDemandPercentComplete = useMemo(() => {
        return getOnDemandCreditPercentComplete(
            timeRecord.percentComplete,
            onDemandProgress,
        );
    }, [onDemandProgress, timeRecord.percentComplete]);

    const percentComplete = timeRecord.isLive ? livePercentComplete : onDemandPercentComplete;

    const onClickSegment = useCallback(() => {
    
        if(timeRecord.prgSegment_SK && !isSingleSegment) {
            const segmentItemSk = launchDetails?.segments
                ?.find(seg => seg.prgSegmentSk === timeRecord.prgSegment_SK)?.itemSk;
            if (segmentItemSk)
                changeSegment({ itemSk: segmentItemSk });
        }

        onClose?.();       
    }, [timeRecord.prgSegment_SK, isSingleSegment, onClose, launchDetails?.segments, changeSegment]);
    
    const headingText = percentComplete === 100 
        ? 'Complete' 
        : (percentComplete === 0 
            ? 'Not started' 
            : (timeRecord.isLive ? 'Partially complete' : 'Continue'));
    const timeCardClass = timeRecord.isLive || !onClose ? 'time-card' : 'time-card time-card__on-demand';
    const completionClass = percentComplete === 100 
        ? 'time-card__details__completion-text-complete sentence-label' 
        : 'time-card__details__completion-text sentence-label';
    
    const isAccreditedSegment = Boolean(timeRecord.trackCle);
    const timecardHeadingText = isAccreditedSegment ? headingText : 'Not for credit';
        
    const ActionWrapper: React.ComponentType<PropsWithChildren> = useMemo(() => {
        return timeRecord.isLive || !onClose
            ? React.Fragment
            : ({ children }) => <button onClick={onClickSegment}>{children}</button>;
    }, [onClickSegment, onClose, timeRecord.isLive]);

    return (
        <li className={timeCardClass}>
            <ActionWrapper>
                <span className={'time-card__main'}>
                    <span className='time-card__details'>    
                        <span className='time-card__details__segment-title paragraph-2--medium'>{timeRecord.title}</span>
                        <span className={completionClass}>{timecardHeadingText}</span>
                    </span>
                    <PercentCircle percentCompleted={percentComplete} isPlayable={!timeRecord.isLive && !!onClose} isHidden={!isAccreditedSegment}/>
                </span>
            </ActionWrapper>
        </li>
    );
};
